/**
 * Safer Lighting — Three.js Hero Particle Field
 * Mouse-interactive with velocity, spring physics
 */
(function () {
  'use strict';

  function bootWhenReady(retries) {
    if (!window.THREE) {
      if ((retries || 0) >= 100) return;
      setTimeout(() => bootWhenReady((retries || 0) + 1), 50);
      return;
    }

    function ParticleField(container) {
      this.el = container;
      const hero = container.closest('.hero-banner') || container;
      this.w = hero.offsetWidth || window.innerWidth;
      this.h = hero.offsetHeight || window.innerHeight;

      this.scene = new THREE.Scene();

      this.camera = new THREE.PerspectiveCamera(75, this.w / this.h, 1, 3000);
      this.camera.position.z = 800;

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(this.w, this.h);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.domElement.style.cssText =
        'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;opacity:1;';
      container.appendChild(this.renderer.domElement);

      this.particles = [];
      this.mouse = new THREE.Vector2(-9999, -9999);
      this.mouseWorld = new THREE.Vector3(0, 0, 0);

      const isMobile = window.innerWidth < 750;
      const particleCount = isMobile ? 400 : 1500;

      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(particleCount * 3);
      const colors = new Float32Array(particleCount * 3);

      // Dynamic Theme Detection
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

      // Brand colors mapped to theme
      const colorPrimary = new THREE.Color(isDark ? 0xd6b059 : 0x0a192f); // Gold in dark mode, Navy in light mode
      const colorSecondary = new THREE.Color(isDark ? 0xb89038 : 0x475569); // Deep Gold in dark mode, Slate in light mode
      const colorAccent = new THREE.Color(isDark ? 0xe4c46a : 0xd6b059); // Luminous Gold in dark mode, Deep Gold in light mode

      for (let i = 0; i < particleCount; i++) {
        const x = (Math.random() - 0.5) * 3000;
        const y = (Math.random() - 0.5) * 3000;
        const z = (Math.random() - 0.5) * 2000;

        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;

        // 70% Primary, 20% Secondary, 10% Accent
        const randColor = Math.random();
        let color = colorPrimary;
        if (randColor > 0.9) color = colorAccent;
        else if (randColor > 0.7) color = colorSecondary;

        color.toArray(colors, i * 3);

        this.particles.push({
          x: x,
          y: y,
          z: z,
          vx: 0,
          vy: 0,
          vz: 0,
          baseSpeed: Math.random() * 0.3 + 0.1,
          phase: Math.random() * Math.PI * 2,
          driftX: (Math.random() - 0.5) * 0.2, // slower X drift
          driftY: Math.random() * 0.3 + 0.1, // slower Y drift (half speed)
          index: i,
        });
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      this.baseColors = new Float32Array(colors);

      // Generate sharp, tiny elegant dot
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.15, 'rgba(255,255,255,0.8)');
      grad.addColorStop(0.4, 'rgba(255,255,255,0.1)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 64, 64);
      const texture = new THREE.CanvasTexture(canvas);

      const material = new THREE.PointsMaterial({
        size: isDark ? 6.0 : 4.5, // larger in dark mode for visibility
        map: texture,
        transparent: true,
        opacity: isDark ? 0.8 : 0.6, // more opaque in dark mode
        vertexColors: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: true,
      });

      this.points = new THREE.Points(geometry, material);
      this.scene.add(this.points);

      this._bind();

      this.clock = new THREE.Clock();
      this._loop();
    }

    ParticleField.prototype._bind = function () {
      const hero = this.el.closest('.hero-banner') || this.el;

      const onMove = (e) => {
        const rect = hero.getBoundingClientRect();
        let clientX = e.clientX;
        let clientY = e.clientY;

        if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
          clientY = e.touches[0].clientY;
        }

        this.mouse.x = ((clientX - rect.left) / this.w) * 2 - 1;
        this.mouse.y = -((clientY - rect.top) / this.h) * 2 + 1;

        const vector = new THREE.Vector3(this.mouse.x, this.mouse.y, 0.5);
        vector.unproject(this.camera);
        const dir = vector.sub(this.camera.position).normalize();
        const distance = -this.camera.position.z / dir.z;
        this.mouseWorld.copy(this.camera.position).add(dir.multiplyScalar(distance));
      };

      hero.addEventListener('mousemove', onMove, { passive: true });
      hero.addEventListener('touchmove', onMove, { passive: true });

      hero.addEventListener('mouseleave', () => {
        this.mouse.x = -9999;
        this.mouse.y = -9999;
        this.mouseWorld.set(0, 0, 9999);
      });

      this._onResize = () => {
        const h = this.el.closest('.hero-banner') || this.el;
        this.w = h.offsetWidth || window.innerWidth;
        this.h = h.offsetHeight || window.innerHeight;
        this.renderer.setSize(this.w, this.h);
        this.camera.aspect = this.w / this.h;
        this.camera.updateProjectionMatrix();
      };
      window.addEventListener('resize', this._onResize);
    };

    ParticleField.prototype._loop = function () {
      this._raf = requestAnimationFrame(() => this._loop());

      const t = this.clock.getElapsedTime();

      const positions = this.points.geometry.attributes.position.array;
      const colors = this.points.geometry.attributes.color.array;

      for (let i = 0; i < this.particles.length; i++) {
        const p = this.particles[i];

        // Continuous elegant fluid drift
        p.x += p.driftX + Math.sin(t * p.baseSpeed + p.phase) * 0.2;
        p.y += p.driftY + Math.cos(t * p.baseSpeed * 0.8 + p.phase) * 0.1;
        p.z += Math.sin(t * p.baseSpeed * 0.5) * 0.3;

        // Wrap around space seamlessly
        if (p.y > 1500) p.y -= 3000;
        if (p.y < -1500) p.y += 3000;
        if (p.x > 1500) p.x -= 3000;
        if (p.x < -1500) p.x += 3000;
        if (p.z > 1000) p.z -= 2000;
        if (p.z < -1000) p.z += 2000;

        let forceX = 0;
        let forceY = 0;
        let forceZ = 0;

        let distToMouse = 9999;
        if (this.mouse.x !== -9999) {
          const dx = p.x - this.mouseWorld.x;
          const dy = p.y - this.mouseWorld.y;
          distToMouse = Math.sqrt(dx * dx + dy * dy);

          // Gentle repulsive repulsion field (no aggressive rubberbanding)
          const maxRadius = 400;
          if (distToMouse < maxRadius) {
            const force = Math.pow(1 - distToMouse / maxRadius, 2) * 5.0;
            forceX = (dx / Math.max(distToMouse, 1)) * force;
            forceY = (dy / Math.max(distToMouse, 1)) * force;
            forceZ = force * 0.5;
          }
        }

        // Smoothly interpolate velocity
        p.vx += (forceX - p.vx) * 0.08;
        p.vy += (forceY - p.vy) * 0.08;
        p.vz += (forceZ - p.vz) * 0.08;

        // Apply calculated positions to buffer
        positions[i * 3] = p.x + p.vx;
        positions[i * 3 + 1] = p.y + p.vy;
        positions[i * 3 + 2] = p.z + p.vz;

        // Subtle twinkle / glow when near mouse
        const baseR = this.baseColors[i * 3];
        const baseG = this.baseColors[i * 3 + 1];
        const baseB = this.baseColors[i * 3 + 2];

        if (distToMouse < 500) {
          const glow = 1.0 - distToMouse / 500;
          colors[i * 3] = baseR + (1.0 - baseR) * glow;
          colors[i * 3 + 1] = baseG + (1.0 - baseG) * glow;
          colors[i * 3 + 2] = baseB + (1.0 - baseB) * glow;
        } else {
          colors[i * 3] = baseR;
          colors[i * 3 + 1] = baseG;
          colors[i * 3 + 2] = baseB;
        }
      }

      this.points.geometry.attributes.position.needsUpdate = true;
      this.points.geometry.attributes.color.needsUpdate = true;

      this.scene.rotation.y = Math.sin(t * 0.1) * 0.1;
      this.scene.rotation.x = Math.cos(t * 0.15) * 0.05;

      this.renderer.render(this.scene, this.camera);
    };

    ParticleField.prototype.destroy = function () {
      cancelAnimationFrame(this._raf);
      window.removeEventListener('resize', this._onResize);
      if (this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
      this.renderer.dispose();
    };

    const container = document.querySelector('.hero-banner__canvas');
    if (!container) return;

    // Force immediate execution, bypassing IntersectionObserver to ensure 100% startup reliability
    try {
      const pf = (container._pf = new ParticleField(container));
      // Stop the render loop while the hero is scrolled out of view.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          const visible = entries[0].isIntersecting;
          if (!visible && pf._raf) {
            cancelAnimationFrame(pf._raf);
            pf._raf = null;
          } else if (visible && !pf._raf) {
            pf._loop();
          }
        }).observe(container);
      }
    } catch (err) {
      console.error('Three.JS Particle failed:', err);
    }
  }

  // Wrap so the DOMContentLoaded Event object isn't passed in as `retries`
  // (that disabled the 100-try cap and left the 50ms poll running forever).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootWhenReady(0));
  } else {
    bootWhenReady(0);
  }
})();
